import { useState, useEffect } from 'react'
import { apiClient } from '@/services/apiClient'
import { SystemParameter, DepositBonusTier, WithdrawalFeeConfig, TradingParameters } from '@/types'
import DepositBonusTab from './tabs/DepositBonusTab'
import WithdrawalFeeTab from './tabs/WithdrawalFeeTab'
import OrderLotsTab from './tabs/OrderLotsTab'
import TradingParametersTab from './tabs/TradingParametersTab'
import PaymentChannelsTab from './tabs/PaymentChannelsTab'
import OperatorTab from './tabs/OperatorTab'
import './ParametersPage.css'

type TabType = 'deposit_bonus' | 'withdrawal_fee' | 'order_lot' | 'trading' | 'payment_channels' | 'operator'

interface TabConfig {
  id: TabType
  label: string
  icon: string
}

const TABS: TabConfig[] = [
  { id: 'deposit_bonus', label: '充值赠金', icon: '🎁' },
  { id: 'withdrawal_fee', label: '提现手续费', icon: '💰' },
  { id: 'order_lot', label: '下单手数', icon: '📊' },
  { id: 'trading', label: '交易参数', icon: '⚙️' },
  { id: 'payment_channels', label: '支付通道', icon: '💳' },
  { id: 'operator', label: '运营商配置', icon: '👥' },
]

export default function ParametersPage() {
  const [activeTab, setActiveTab] = useState<TabType>('deposit_bonus')
  const [parameters, setParameters] = useState<SystemParameter[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  // Load parameters on mount
  useEffect(() => {
    loadParameters()
  }, [])

  const loadParameters = async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await apiClient.getSystemParameters()
      if (response.success && response.data) {
        setParameters(response.data)
      } else {
        setError(response.message || '加载参数失败')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '加载参数失败')
    } finally {
      setLoading(false)
    }
  }

  const handleParameterUpdate = async (key: string, value: string) => {
    try {
      const response = await apiClient.updateSystemParameter(key, value)
      if (response.success) {
        setSuccessMessage('参数更新成功')
        setTimeout(() => setSuccessMessage(null), 3000)
        // Reload parameters
        await loadParameters()
      } else {
        setError(response.message || '更新失败')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '更新失败')
    }
  }

  const getParametersByCategory = (category: TabType) => {
    return parameters.filter(p => p.category === category)
  }

  const renderTabContent = () => {
    const categoryParams = getParametersByCategory(activeTab)

    switch (activeTab) {
      case 'deposit_bonus':
        return (
          <DepositBonusTab
            parameters={categoryParams}
            onUpdate={handleParameterUpdate}
          />
        )
      case 'withdrawal_fee':
        return (
          <WithdrawalFeeTab
            parameters={categoryParams}
            onUpdate={handleParameterUpdate}
          />
        )
      case 'order_lot':
        return (
          <OrderLotsTab
            parameters={categoryParams}
            onUpdate={handleParameterUpdate}
          />
        )
      case 'trading':
        return (
          <TradingParametersTab
            parameters={categoryParams}
            onUpdate={handleParameterUpdate}
          />
        )
      case 'payment_channels':
        return <PaymentChannelsTab />
      case 'operator':
        return (
          <OperatorTab
            parameters={categoryParams}
            onUpdate={handleParameterUpdate}
          />
        )
      default:
        return null
    }
  }

  return (
    <div className="parameters-page">
      {/* Header */}
      <div className="page-header">
        <div className="header-content">
          <h1>参数配置</h1>
          <p>管理系统关键参数</p>
        </div>
        <button className="btn-refresh" onClick={loadParameters} disabled={loading}>
          {loading ? '加载中...' : '刷新'}
        </button>
      </div>

      {/* Messages */}
      {error && (
        <div className="alert alert-error">
          <span>❌ {error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}
      {successMessage && (
        <div className="alert alert-success">
          <span>✅ {successMessage}</span>
          <button onClick={() => setSuccessMessage(null)}>×</button>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="tab-navigation">
        {TABS.map(tab => (
          <button
            key={tab.id}
            className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <span className="tab-icon">{tab.icon}</span>
            <span className="tab-label">{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>加载中...</p>
          </div>
        ) : (
          renderTabContent()
        )}
      </div>
    </div>
  )
}
